import * as Y from "yjs";
import { WebsocketProvider } from "y-websocket";
import { useEffect, useRef } from "react";

/**
 * Syncs guidelines via a single Y.Map('guidelines') with one key: 'list'
 * We store the whole guidelines array in that key.
 * Guideline shape: { id: string, type: "vertical"|"horizontal", position: number }
 */
export default function useCollabGuidelines(projectId, onRemoteUpdate) {
  const ydocRef = useRef(null);
  const providerRef = useRef(null);
  const yMapRef = useRef(null);

  const cbRef = useRef(onRemoteUpdate);
  useEffect(() => {
    cbRef.current = onRemoteUpdate;
  }, [onRemoteUpdate]);

  // used to ignore our own echoes
  const lastSentHashRef = useRef("");

  const stableSort = (arr) =>
    [...arr].sort((a, b) => {
      if (a.id !== b.id) return String(a.id).localeCompare(String(b.id));
      if (a.type !== b.type)
        return String(a.type).localeCompare(String(b.type));
      return (a.position ?? 0) - (b.position ?? 0);
    });

  const normalize = (arr) =>
    stableSort(
      (Array.isArray(arr) ? arr : []).map((g) => ({
        id: String(g.id || ""),
        type: g.type === "horizontal" ? "horizontal" : "vertical",
        position: Number.isFinite(g.position) ? Number(g.position) : 0,
      }))
    );

  const stableHash = (obj) => {
    try {
      return JSON.stringify(obj);
    } catch {
      return String(Date.now());
    }
  };

  useEffect(() => {
    if (!projectId) return;

    const ydoc = new Y.Doc();

    // clean socket URL (avoid trailing slashes or quotes)
    const raw = process.env.REACT_APP_SOCKET_URL || "ws://localhost:1234";
    const socketUrl = raw.replace(/['\"\s]+/g, "").replace(/\/+$/g, "");
    const room = String(projectId).trim();

    const provider = new WebsocketProvider(socketUrl, room, ydoc);
    const yMap = ydoc.getMap("guidelines"); // <— Y.Map, not Array

    const emit = () => {
      const list = yMap.get("list") || [];
      const safe = normalize(list);
      const h = stableHash(safe);
      if (h === lastSentHashRef.current) return; // our own write
      cbRef.current?.(safe);
    };

    const observer = () => emit();

    yMap.observe(observer);
    emit(); // initial (in case room already has state)

    // (optional) small status logs to debug connectivity quickly
    provider.on("status", (e) => {
      // console.log("[y-websocket] status:", e.status);
    });

    ydocRef.current = ydoc;
    providerRef.current = provider;
    yMapRef.current = yMap;

    return () => {
      try {
        yMap.unobserve(observer);
      } catch {}
      try {
        provider.destroy();
      } catch {}
      try {
        ydoc.destroy();
      } catch {}
      ydocRef.current = null;
      providerRef.current = null;
      yMapRef.current = null;
    };
  }, [projectId]);

  /**
   * Push local guidelines to the room (atomic replace).
   */
  const syncGuidelines = (nextArr = []) => {
    const yMap = yMapRef.current;
    const ydoc = ydocRef.current;
    if (!yMap || !ydoc) return;

    const normalized = normalize(nextArr);
    const h = stableHash(normalized);

    ydoc.transact(() => {
      // single write -> yMap.set('list', <normalized array>)
      yMap.set("list", normalized);
    });

    // mark this hash as ours to ignore echo on this client
    lastSentHashRef.current = h;
  };

  return { syncGuidelines };
}
